/**
 * Why a ready quest can't be turned in, decided before the click (plan 06
 * §2.4): the same reward-fit plan the server's turn-in gate runs
 * (src/shared/quests/questRewardCapacity.ts), against the bag the client
 * holds. The server stays the authority; this only explains in advance.
 */
import type { InventoryItem } from '../../../../shared/CharacterTypes';
import type { Quest } from '../../../../shared/NPCData';
import { QUEST_DATABASE } from '../../../../shared/content/generated/quests.gen';
import { ITEM_DATABASE } from '../../../../shared/ItemData';
import type { BagLike, BagStack } from '../../../../shared/inventory/bagCapacity';
import { planQuestRewards } from '../../../../shared/quests/questRewardCapacity';

export interface TurnInBlock {
  /** Button text, e.g. "Bag full: free 2 slots". */
  label: string;
  /** Sentence under the button naming what needs the room. */
  detail: string;
}

/** The client bag as the shared planner reads it: one entry per item id. */
export function bagFromItems(items: readonly InventoryItem[]): BagLike {
  const bag = new Map<string, BagStack>();
  for (const item of items) {
    const prev = bag.get(item.id);
    bag.set(item.id, { quantity: (prev?.quantity ?? 0) + item.quantity, maxStack: item.maxStack ?? prev?.maxStack });
  }
  return bag;
}

export function turnInBlockFor(quest: Quest, items: readonly InventoryItem[], maxSlots: number): TurnInBlock | null {
  if (!quest.isAccepted || !quest.isTurnInReady || quest.isCompleted) return null;
  const def = QUEST_DATABASE[quest.id];
  if (!def) return null;
  const plan = planQuestRewards(bagFromItems(items), def, maxSlots, quest.rewardClass);
  if (plan.ok || plan.reason !== 'bag_full') return null;
  const slots = Math.max(1, Math.ceil(plan.missingSlots));
  const plural = slots === 1 ? 'slot' : 'slots';
  const names = plan.blockingItemIds
    .map(id => (ITEM_DATABASE as Record<string, { name?: string } | undefined>)[id]?.name || id);
  return {
    label: `Bag full: free ${slots} ${plural}`,
    detail: names.length > 0
      ? `Your bag is ${plan.usedSlots} / ${plan.maxSlots}. Free ${slots} ${plural} for ${names.join(', ')} to turn this in.`
      : `Your bag is ${plan.usedSlots} / ${plan.maxSlots}. Free ${slots} ${plural} to turn this in.`,
  };
}
